import { Observable } from 'rxjs';
import { ExtendedMap } from './ext-map.type';
import { ButtonParams } from './button.types';
import { PluginIDs } from './plugin-ids.type';
import { LegendParams } from './legend.types';
import { WidgetParams } from './widget.interface';
import { JepLayer } from '../esri/arcgis-types/jep-arcgis.types';

export type PluginID = PluginIDs | string;

export interface PluginService {
  id?: PluginID;
  create: (...args: any) => Plugin | Observable<Plugin>;
}

export class Plugin<
  L extends JepLayer = JepLayer,
  T extends PluginID = PluginID
> {
  id: T;
  button?: ButtonParams;
  legend?: LegendParams;
  widget?: WidgetParams;
  layer?: L;
  children?: PluginMap;

  constructor(plugin: {
    id: T;
    button?: ButtonParams;
    legend?: LegendParams;
    widget?: WidgetParams;
    layer?: L;
    children?: Plugin[] | PluginMap;
  }) {
    this.id = plugin.id;

    this.button = plugin.button;
    if (this.button) {
      this.button.id = this.id;
    }

    this.legend = plugin.legend;
    if (this.legend) {
      this.legend.id = this.id;
    }

    this.widget = plugin.widget;
    if (this.widget) {
      this.widget.id = this.id;
    }

    this.layer = plugin.layer;
    if (this.layer) {
      this.layer.id = this.id;
    }

    if (plugin.children) {
      if (plugin.children instanceof PluginMap) {
        this.children = plugin.children;
      } else if (Array.isArray(plugin.children) && plugin.children.length > 0) {
        this.children = new PluginMap(
          plugin.children.map((child) => [child.id, child])
        );
      }
    }
  }
}

export class PluginMap extends ExtendedMap<Plugin> {
  constructor(
    entries?: readonly (readonly [PluginID, Plugin])[] | ExtendedMap<Plugin>
  ) {
    super(
      entries instanceof ExtendedMap ? Array.from(entries.entries()) : entries
    );
  }

  allLegends = () => this._traversePluginMap<LegendParams>('legend');
  allButtons = () => this._traversePluginMap<ButtonParams>('button');
  allLayers = () => this._traversePluginMap<JepLayer>('layer');
  allWidgets = () => this._traversePluginMap<WidgetParams>('widget');

  find(partialPlugin: Partial<Plugin>): {
    plugin: Plugin | null;
    path: { matchProperties: Partial<Plugin>; id: string }[];
  } {
    const findPluginRecursive = (
      pluginMap: PluginMap,
      path: { matchProperties: Partial<Plugin>; id: string }[] = []
    ): {
      plugin: Plugin | null;
      path: { matchProperties: Partial<Plugin>; id: string }[];
    } => {
      for (const [id, plugin] of pluginMap) {
        const currentPath = [...path, { matchProperties: partialPlugin, id }];

        if (this._matchesPlugin(plugin, partialPlugin)) {
          return { plugin, path: currentPath };
        }

        if (plugin.children) {
          const nestedResult = findPluginRecursive(
            plugin.children,
            currentPath
          );
          if (nestedResult.plugin) {
            return nestedResult;
          }
        }
      }

      return { plugin: null, path: [] };
    };

    return findPluginRecursive(this);
  }

  private _matchesPlugin(
    plugin: Plugin,
    partialPlugin: Partial<Plugin>
  ): boolean {
    return Object.entries(partialPlugin).every(([key, value]) => {
      if (key === 'children') return false;

      const pluginValue = plugin[key as keyof Plugin];

      if (typeof value === 'object' && value !== null) {
        return this._matchesNestedObject(pluginValue, value);
      }

      return pluginValue === value;
    });
  }

  private _matchesNestedObject(pluginValue: any, value: object): boolean {
    return Object.entries(value).every(
      ([k, v]) => pluginValue && pluginValue[k] === v
    );
  }

  private _traversePluginMap<T>(field: keyof Plugin): ExtendedMap<T> {
    const all = new ExtendedMap<T>();

    for (const [id, plugin] of this) {
      if (plugin[field]) {
        all.set(id, plugin[field] as T);
      }

      if (plugin.children) {
        const childrenMap = plugin.children._traversePluginMap<T>(field);
        for (const [childId, childData] of childrenMap) {
          all.set(childId, childData);
        }
      }
    }

    return all;
  }
}
